import { css, Global } from '@emotion/react';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { lazy, StrictMode, Suspense, useContext } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter, Navigate, Route, Routes } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { ApplicationContext } from '~/shared/context/context';
import type { AppEnvironment } from '~/shared/environment/environment';
import { PageWithTitle } from '~/shared/page-with-title/PageWithTitle';

import { agentsRoute, loadAgentsLibraryPage } from '../agents';
import { aiTransformationRoute, loadAiTransformationPage } from '../ai-transformation';
import { companyHomeFeatureFlags, companyHomeRoute, loadCompanyHomePage } from '../company-home';
import { knowledgeBaseRoute, usersAndRolesRoute } from '../company-settings/route-config';
import { ExternalLinkConfirmation } from '../components/ExternalLinkConfirmation';
import { debugRoute } from '../debug/route-config';
import { FatalErrorFallback } from '../FatalErrorFallback';
import { ApplicationFetchersProvider } from '../fetchers/ApplicationFetchersContext';
import { graphCanvasRoute, loadGraphCanvasPage } from '../graph-canvas';
import { growthRoute, loadGrowthPage } from '../growth';
import { jobProfileManagementV2Route } from '../job-architecture-v2/job-profile-management/route';
import { RedirectToJobArchitectureV2 } from '../job-architecture-v2/RedirectToJobArchitectureV2';
import { LandingPage } from '../LandingPage';
import { loadPersonalHomePage, personalHomeRoute } from '../personal-home';
import { loadMyAlignmentPage, loadTeamAlignmentPage, positionAlignmentRoute } from '../position-alignment';
import {
    loadPositionManagementPage,
    loadPositionSetupWizardPage,
    POSITION_SETUP_ROUTE_SEGMENT,
    positionManagementRoute,
} from '../position-management';
import { MixpanelTrackingProvider } from '../tracking/mixpanel/MixpanelTrackingProvider';
import { UrlTileQueryAdapter } from '../url-tiles/UrlTileQueryAdapter';
import type { AppDependencies } from './AppWithContext';
import { AppWithContext } from './AppWithContext';
import { ColorSchemeProvider } from './color-scheme/ColorSchemeProvider';
import { DataRouteWithFeatureFlagEnabled } from './DataRouteWithFeatureFlagEnabled';
import { DashboardAccessGuard } from './dashboard-visibility/DashboardAccessGuard';
import { snackbarComponents } from './error-handling/SnackbarComponents';
import { FeatureFlagInitializer } from './FeatureFlagInitializer';
import { KeyboardShortcutsPolicyProvider } from './KeyboardShortcutsPolicyProvider';
import type { LayoutTiling, LayoutVariant } from './Layout';
import { lazyRoutesConfig } from './lazy-routes-config';
import { PageWithPageViewTracking } from './PageWithPageViewTracking';
import { route as dashboardPageRoute } from './pages/dashboard/constants';
import { route as dashboardV2Route } from './pages/dashboard-v2/constants';
import { route as globalSearchRoute } from './pages/global-search-page/constants';
import { route as profilePageRoute, userProfileUrl } from './pages/profile/constants';
import { route as talentDirectoryPageRoute } from './pages/talent-directory/constants';
import { createRoutesFromRouteDefinitions } from './routes-from-route-definitions';
import { TableZoomPolicyProvider } from './TableZoomPolicyProvider';

const AccessDeniedPage = lazy(async () => {
    const { AccessDeniedPage } = await import('../AccessDeniedPage');

    return { default: AccessDeniedPage };
});

const DeferredMuiProviders = lazy(async () => {
    const { DeferredMuiProviders } = await import('./DeferredMuiProviders');

    return { default: DeferredMuiProviders };
});

const Error404 = lazy(async () => {
    const { Error404 } = await import('./error-handling/ErrorPages');

    return { default: Error404 };
});

const GlobalSearchPage = lazy(async () => {
    const { GlobalSearchPage } = await import('./pages/global-search-page/GlobalSearch');

    return { default: GlobalSearchPage };
});

const Layout = lazy(async () => {
    const { Layout } = await import('./Layout');

    return { default: Layout };
});

const SkillFeedbackModalRoot = lazy(async () => {
    const { SkillFeedbackModalRoot } = await import('../legacy/feedback/SkillFeedbackModalRoot');

    return { default: SkillFeedbackModalRoot };
});

const TalentDirectoryPage = lazy(async () => {
    const { TalentDirectoryPage } = await import('./pages/talent-directory/TalentDirectory');

    return { default: TalentDirectoryPage };
});

const TerminologyInitializer = lazy(async () => {
    const { TerminologyInitializer } = await import(
        '../company-settings/tabs/organization-tab/terminology/TerminologyInitializer'
    );

    return { default: TerminologyInitializer };
});

type AppBaseProperties = {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutTiling?: LayoutTiling;
    readonly layoutVariant?: LayoutVariant;
    readonly shouldShowFeedbackAction?: boolean;
};

const AuthenticatedAppProviders: FunctionComponent<PropsWithChildren> = ({ children }) => (
    <Suspense fallback={null}>
        <DeferredMuiProviders>{children}</DeferredMuiProviders>
    </Suspense>
);

const RootErrorFallback: FunctionComponent<Pick<AppDependencies, 'translate'>> = ({ translate }) => (
    <FatalErrorFallback message={translate('errors.status.500.message')} title={translate('errors.status.500.title')} />
);

const renderRootErrorFallback = (dependencies: AppDependencies): FunctionComponent<FallbackProps> => {
    const Wrapped: FunctionComponent<FallbackProps> = () => <RootErrorFallback translate={dependencies.translate} />;

    return Wrapped;
};

const AppBase: FunctionComponent<AppBaseProperties> = (properties) => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { featureFlagService, myUserProfile } = applicationState;

    if (myUserProfile.isNothing) {
        return null;
    }

    if (properties.isDataRoute) {
        return (
            <Layout
                hasNavigationMenu={properties.hasNavigationMenu}
                shouldShowFeedbackAction={properties.shouldShowFeedbackAction}
                tiling={properties.layoutTiling}
                variant={properties.layoutVariant}
            />
        );
    }

    return (
        <Routes>
            <Route element={<Layout />}>
                {createRoutesFromRouteDefinitions(
                    { translate },
                    { featureFlagService, routeDefinitions: lazyRoutesConfig },
                )}
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.users}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.profile')}>
                            <Navigate replace={true} to={userProfileUrl(myUserProfile.value.id)} />
                        </PageWithTitle>
                    }
                    path={profilePageRoute.patterns.usersMe}
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('pages.global-search.search-headline')}>
                                <GlobalSearchPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={globalSearchRoute.patterns.search}
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                        </PageWithTitle>
                    }
                    path="/login"
                />
                <Route
                    element={
                        <PageWithTitle title={translate('navigation.dashboard')}>
                            <Navigate replace={true} to={dashboardPageRoute.patterns.dashboard} />
                        </PageWithTitle>
                    }
                    path="/"
                />
                <Route
                    element={
                        <PageWithPageViewTracking>
                            <PageWithTitle title={translate('navigation.talent-directory')}>
                                <TalentDirectoryPage />
                            </PageWithTitle>
                        </PageWithPageViewTracking>
                    }
                    path={talentDirectoryPageRoute.patterns.talentDirectory}
                />
                <Route element={<Error404 />} path="*" />
            </Route>
        </Routes>
    );
};

type AppContentProperties = {
    readonly dependencies: AppDependencies;
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutTiling?: LayoutTiling;
    readonly layoutVariant?: LayoutVariant;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const AppContent: FunctionComponent<AppContentProperties> = ({
    dependencies,
    hasNavigationMenu = true,
    isDataRoute,
    layoutTiling,
    layoutVariant,
    shouldShowFeedbackFloatingActionButton = true,
}) => {
    const { authorizationProvider, translate } = dependencies;
    const fallbackDocumentTitle = translate('common.brand');
    const isMobile = false;

    if (authorizationProvider.isBlocked === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AuthenticatedAppProviders>
                    <AccessDeniedPage />
                </AuthenticatedAppProviders>
            </PageWithTitle>
        );
    }

    if (isMobile) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AuthenticatedAppProviders>
                    <AppBase
                        hasNavigationMenu={hasNavigationMenu}
                        isDataRoute={isDataRoute}
                        layoutTiling={layoutTiling}
                        layoutVariant={layoutVariant}
                    />
                </AuthenticatedAppProviders>
            </PageWithTitle>
        );
    }

    if (authorizationProvider.isLoggedIn === true) {
        return (
            <PageWithTitle title={fallbackDocumentTitle}>
                <AuthenticatedAppProviders>
                    <FeatureFlagInitializer featureFlagService={dependencies.featureFlagService}>
                        <KeyboardShortcutsPolicyProvider>
                            <TableZoomPolicyProvider>
                                <TerminologyInitializer />
                                <SkillFeedbackModalRoot
                                    isWithFloatingActionButton={shouldShowFeedbackFloatingActionButton}
                                >
                                    <AppBase
                                        hasNavigationMenu={hasNavigationMenu}
                                        isDataRoute={isDataRoute}
                                        layoutTiling={layoutTiling}
                                        layoutVariant={layoutVariant}
                                        shouldShowFeedbackAction={shouldShowFeedbackFloatingActionButton}
                                    />
                                </SkillFeedbackModalRoot>
                            </TableZoomPolicyProvider>
                        </KeyboardShortcutsPolicyProvider>
                    </FeatureFlagInitializer>
                </AuthenticatedAppProviders>
            </PageWithTitle>
        );
    }

    return (
        <PageWithTitle title={fallbackDocumentTitle}>
            <LandingPage />
        </PageWithTitle>
    );
};

type AppProperties = {
    readonly dependencies: AppDependencies;
    readonly environment: AppEnvironment;
    readonly queryClient: QueryClient;
};

type RootProperties = AppProperties & {
    readonly hasNavigationMenu?: boolean;
    readonly isDataRoute: boolean;
    readonly layoutTiling?: LayoutTiling;
    readonly layoutVariant?: LayoutVariant;
    readonly pageTitle: string;
    readonly shouldShowFeedbackFloatingActionButton?: boolean;
};

const appCanvasStyles = css`
    html,
    body,
    #app {
        min-height: 100%;
        background-color: var(--color-surface-canvas);
    }

    #app {
        min-height: 100vh;
    }

    ::view-transition-group(*) {
        animation-duration: 400ms;
        animation-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
    }
`;

const Root: FunctionComponent<RootProperties> = (properties) => {
    const {
        dependencies,
        environment,
        hasNavigationMenu,
        isDataRoute,
        layoutTiling,
        layoutVariant,
        pageTitle,
        queryClient,
        shouldShowFeedbackFloatingActionButton,
    } = properties;

    return (
        <StrictMode>
            <ColorSchemeProvider>
                <QueryClientProvider client={queryClient}>
                    <PageWithTitle title={pageTitle}>
                        <ExternalLinkConfirmation translate={dependencies.translate}>
                            <ErrorBoundary FallbackComponent={renderRootErrorFallback(dependencies)}>
                                <AppWithContext dependencies={dependencies} environment={environment}>
                                    <ApplicationFetchersProvider>
                                        <MixpanelTrackingProvider>
                                            <SnackbarProvider Components={snackbarComponents} maxSnack={3}>
                                                <Global styles={appCanvasStyles} />
                                                <AppContent
                                                    dependencies={dependencies}
                                                    hasNavigationMenu={hasNavigationMenu}
                                                    isDataRoute={isDataRoute}
                                                    layoutTiling={layoutTiling}
                                                    layoutVariant={layoutVariant}
                                                    shouldShowFeedbackFloatingActionButton={
                                                        shouldShowFeedbackFloatingActionButton
                                                    }
                                                />
                                            </SnackbarProvider>
                                        </MixpanelTrackingProvider>
                                    </ApplicationFetchersProvider>
                                </AppWithContext>
                            </ErrorBoundary>
                        </ExternalLinkConfirmation>
                    </PageWithTitle>
                </QueryClientProvider>
            </ColorSchemeProvider>
        </StrictMode>
    );
};

// The team's member feed keeps a narrow column beside the alignment tile, which takes the rest of the row.
const teamAlignmentLayoutTiling: LayoutTiling = {
    mainMinWidth: 'calc(28rem + 2 * var(--spacing-m))',
};

// The position graph and list, like the alignment's content column and comment rail, sit side by side at a usable
// width, so opening a side tile scrolls the tiling row instead of squeezing them.
const positionManagementLayoutTiling: LayoutTiling = {
    mainMinWidth: 'max(50%, 64rem)',
};

export const App: FunctionComponent<AppProperties> = (properties) => {
    const { dependencies, environment, queryClient } = properties;
    const { translate } = dependencies;

    const router = createBrowserRouter([
        {
            element: <RedirectToJobArchitectureV2 />,
            path: '/job-and-skill-architecture/*',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { CareerFrameworksPage } = await import('./pages/career-frameworks/CareerFrameworksPage');

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="leveling-framework-menu-item">
                                    <CareerFrameworksPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { NewCareerFrameworkPage } = await import(
                            './pages/career-frameworks/NewCareerFrameworkPage'
                        );

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="leveling-framework-menu-item">
                                    <NewCareerFrameworkPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                    path: 'new',
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { CareerFrameworksGetStartedPage } = await import(
                            './pages/career-frameworks/CareerFrameworksGetStartedPage'
                        );

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="leveling-framework-menu-item">
                                    <CareerFrameworksGetStartedPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                    path: 'get-started',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.career-frameworks')}
                    queryClient={queryClient}
                />
            ),
            path: '/career-frameworks',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSelectPage } = await import(
                            '../job-architecture-v2/select-page/JobArchitectureSelectPage'
                        );

                        return {
                            Component: JobArchitectureSelectPage,
                        };
                    },
                },
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewTab } = await import('../job-architecture-v2/tabs/overview');

                                return {
                                    Component: OverviewTab,
                                };
                            },
                            path: 'overview',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { JobArchitectureSetupPage } = await import(
                            '../job-architecture-v2/JobArchitectureSetupPage'
                        );

                        return {
                            Component: JobArchitectureSetupPage,
                        };
                    },
                    path: ':id',
                },
                {
                    children: [
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { CompanyInformationPage } = await import(
                                    '../job-architecture-v2/setup-dialog/CompanyInformationPage'
                                );

                                return {
                                    Component: CompanyInformationPage,
                                };
                            },
                            path: 'company-information',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OverviewPage } = await import(
                                    '../job-architecture-v2/setup-dialog/OverviewPage'
                                );

                                return {
                                    Component: OverviewPage,
                                };
                            },
                            path: 'overview',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { UploadDataPage } = await import(
                                    '../job-architecture-v2/setup-dialog/UploadDataPage'
                                );

                                return {
                                    Component: UploadDataPage,
                                };
                            },
                            path: 'upload-data',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { SetupDialogLayout } = await import(
                            '../job-architecture-v2/setup-dialog/SetupDialogLayout'
                        );

                        return {
                            Component: SetupDialogLayout,
                        };
                    },
                    path: ':id/setup-dialog',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-architecture-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { SkillsPage } = await import('../job-architecture-v2/skills');

                        return {
                            Component: SkillsPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.skills')}
                    queryClient={queryClient}
                />
            ),
            path: '/skills',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { TasksPage } = await import('../job-architecture-v2/tasks');

                        return {
                            Component: TasksPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.tasks')}
                    queryClient={queryClient}
                />
            ),
            path: '/tasks',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: GrowthPage } = await loadGrowthPage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="growth-goals">
                                    <GrowthPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.growth-goals')}
                    queryClient={queryClient}
                />
            ),
            path: growthRoute.patterns.growth,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: PersonalHomePage } = await loadPersonalHomePage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="personal-home">
                                    <PersonalHomePage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="canvas"
                    pageTitle={translate('navigation.home')}
                    queryClient={queryClient}
                />
            ),
            path: personalHomeRoute.patterns.personalHome,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: CompanyHomePage } = await loadCompanyHomePage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName={companyHomeFeatureFlags}>
                                    <CompanyHomePage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="canvas"
                    pageTitle={translate('navigation.home')}
                    queryClient={queryClient}
                />
            ),
            path: companyHomeRoute.patterns.companyHome,
        },
        {
            children: [
                {
                    index: true,
                    lazy: loadAgentsLibraryPage,
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.agents')}
                    queryClient={queryClient}
                />
            ),
            path: agentsRoute.patterns.agents,
        },
        {
            children: [
                {
                    index: true,
                    lazy: loadAiTransformationPage,
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.ai-transformation')}
                    layoutTiling={{ tileGap: 'var(--spacing-s)' }}
                    queryClient={queryClient}
                />
            ),
            path: aiTransformationRoute.patterns.aiTransformation,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: GraphCanvasPage } = await loadGraphCanvasPage();

                        return {
                            Component: GraphCanvasPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.graph-canvas')}
                    queryClient={queryClient}
                />
            ),
            path: graphCanvasRoute.patterns.graphCanvas,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: MyAlignmentPage } = await loadMyAlignmentPage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="position-alignment">
                                    <MyAlignmentPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutTiling={positionManagementLayoutTiling}
                    layoutVariant="full-bleed"
                    pageTitle={translate('pages.position-alignment.title-me')}
                    queryClient={queryClient}
                />
            ),
            path: positionAlignmentRoute.patterns.me,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: TeamAlignmentPage } = await loadTeamAlignmentPage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="position-alignment">
                                    <TeamAlignmentPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutTiling={teamAlignmentLayoutTiling}
                    layoutVariant="full-bleed"
                    pageTitle={translate('pages.position-alignment.title-team')}
                    queryClient={queryClient}
                />
            ),
            path: positionAlignmentRoute.patterns.team,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Component: PositionManagementPage } = await loadPositionManagementPage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="position-management-v-2">
                                    <PositionManagementPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                },
                {
                    lazy: async () => {
                        const { Component: PositionSetupWizardPage } = await loadPositionSetupWizardPage();

                        return {
                            Component: () => (
                                <DataRouteWithFeatureFlagEnabled enabledFeatureName="position-management-v-2">
                                    <PositionSetupWizardPage />
                                </DataRouteWithFeatureFlagEnabled>
                            ),
                        };
                    },
                    path: POSITION_SETUP_ROUTE_SEGMENT,
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutTiling={positionManagementLayoutTiling}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.position-management')}
                    queryClient={queryClient}
                />
            ),
            path: positionManagementRoute.patterns.positionManagement,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobProfileManagementV2Page } = await import('../job-architecture-v2');

                        return {
                            Component: JobProfileManagementV2Page,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate(jobProfileManagementV2Route.titleTranslationKey)}
                    queryClient={queryClient}
                />
            ),
            path: jobProfileManagementV2Route.patterns.jobProfileManagementV2,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { LearningManagementPage } = await import('../learning-opportunities-v2');

                        return {
                            Component: LearningManagementPage,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.learning-management')}
                    queryClient={queryClient}
                />
            ),
            path: '/learning-opportunities-v2',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { JobProfileTileHostPage } = await import(
                            '../job-architecture-v2/job-profile-tile-host/JobProfileTileHostPage'
                        );

                        return {
                            Component: JobProfileTileHostPage,
                        };
                    },
                },
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { JobProfileRedirectPage } = await import(
                            '../job-architecture-v2/job-profile-tile-host/JobProfileRedirectPage'
                        );

                        return {
                            Component: JobProfileRedirectPage,
                        };
                    },
                    path: ':architectureId/:itemType/:itemId',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="canvas"
                    pageTitle={translate('navigation.job-architecture')}
                    queryClient={queryClient}
                />
            ),
            path: '/job-profile',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { DebugPage } = await import('../debug/DebugPage');

                        return { Component: DebugPage };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('pages.debug.title')}
                    queryClient={queryClient}
                />
            ),
            path: debugRoute.patterns.debug,
        },
        {
            children: [
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { CompanySettingsIndexRedirect } = await import(
                                    '../company-settings/CompanySettingsIndexRedirect'
                                );

                                return { Component: CompanySettingsIndexRedirect };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { OrganizationTab } = await import(
                                    '../company-settings/tabs/organization-tab/OrganizationTab'
                                );

                                return { Component: OrganizationTab };
                            },
                            path: 'organization',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { JobArchitectureTab } = await import(
                                    '../company-settings/tabs/job-architecture-tab/JobArchitectureTab'
                                );

                                return { Component: JobArchitectureTab };
                            },
                            path: 'job-architecture/:jobArchitectureId?',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { JobArchitectureTab } = await import(
                                    '../company-settings/tabs/job-architecture-tab/JobArchitectureTab'
                                );

                                return { Component: JobArchitectureTab };
                            },
                            path: 'job-architecture/:jobArchitectureId?/job-profile/:jobProfileView',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { PersonalPreferencesTab } = await import(
                                    '../company-settings/tabs/personal-preferences-tab/PersonalPreferencesTab'
                                );

                                return { Component: PersonalPreferencesTab };
                            },
                            path: 'personal-preferences',
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { SubscriptionBillingTab } = await import(
                                    '../company-settings/tabs/subscription-billing-tab/SubscriptionBillingTab'
                                );

                                return { Component: SubscriptionBillingTab };
                            },
                            path: 'subscription-billing',
                        },
                        {
                            lazy: async () => {
                                const { PositionArchitectureTab } = await import(
                                    '../company-settings/tabs/position-architecture-tab/PositionArchitectureTab'
                                );

                                return { Component: PositionArchitectureTab };
                            },
                            path: 'position-architecture',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { CompanySettingsRoot } = await import('../company-settings/CompanySettingsRoot');

                        return { Component: CompanySettingsRoot };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.company-settings')}
                    queryClient={queryClient}
                />
            ),
            path: '/companySettings',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { UsersAndRolesPage } = await import('../company-settings/UsersAndRolesPage');

                        return { Component: UsersAndRolesPage };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.users-and-roles')}
                    queryClient={queryClient}
                />
            ),
            path: usersAndRolesRoute.patterns.usersAndRoles,
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        // prettier-ignore
                        const { KnowledgeBasePage } = await import('../company-settings/KnowledgeBasePage');

                        return { Component: KnowledgeBasePage };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="full-bleed"
                    pageTitle={translate('navigation.knowledge-base')}
                    queryClient={queryClient}
                />
            ),
            path: knowledgeBaseRoute.patterns.knowledgeBase,
        },
        {
            children: [
                {
                    lazy: async () => {
                        // prettier-ignore
                        const { OnboardingPage } = await import('./pages/onboarding/OnboardingNew');

                        return {
                            Component: OnboardingPage,
                        };
                    },
                    path: '*',
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    hasNavigationMenu={false}
                    isDataRoute={true}
                    pageTitle={translate('pages.onboarding.headline')}
                    queryClient={queryClient}
                    shouldShowFeedbackFloatingActionButton={false}
                />
            ),
            path: '/onboarding/*',
        },
        {
            children: [
                {
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                // prettier-ignore
                                const { DocumentationLandingPage } = await import(
                                    './pages/documentation/DocumentationLandingPage'
                                );

                                return {
                                    Component: DocumentationLandingPage,
                                };
                            },
                        },
                        {
                            lazy: async () => {
                                // prettier-ignore
                                const { DocumentationArticlePage } = await import(
                                    './pages/documentation/pages/DocumentationArticlePage'
                                );

                                return {
                                    Component: DocumentationArticlePage,
                                };
                            },
                            path: ':slug',
                        },
                    ],
                    lazy: async () => {
                        // prettier-ignore
                        const { DocumentationLayout } = await import('./pages/documentation/DocumentationLayout');

                        return {
                            Component: DocumentationLayout,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    pageTitle={translate('pages.documentation.title')}
                    queryClient={queryClient}
                />
            ),
            path: '/documentation',
        },
        {
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { DashboardV2 } = await import('../dashboard-v2');

                        const GuardedDashboardV2: FunctionComponent = () => (
                            <DashboardAccessGuard dashboard="new">
                                <DashboardV2 />
                            </DashboardAccessGuard>
                        );

                        return {
                            Component: GuardedDashboardV2,
                        };
                    },
                },
            ],
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={true}
                    layoutVariant="canvas"
                    pageTitle={translate('navigation.dashboard')}
                    queryClient={queryClient}
                />
            ),
            path: dashboardV2Route.patterns.dashboardV2,
        },
        {
            element: (
                <Root
                    dependencies={dependencies}
                    environment={environment}
                    isDataRoute={false}
                    pageTitle={translate('common.brand')}
                    queryClient={queryClient}
                />
            ),
            path: '*',
        },
    ]);

    return (
        <UrlTileQueryAdapter>
            <RouterProvider router={router} />
        </UrlTileQueryAdapter>
    );
};
